import { Link, createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { ORDER_STATES, STATE_LABELS, type OpsSummary, type OrderState } from '@hedgehog/shared'
import { opsApi } from '@/components/ops/api'
import { BarTable } from '@/components/ops/bars'
import { formatInt, formatPct, formatUsd } from '@/components/ops/format'
import { Muted, Section } from '@/components/ops/section'
import { ErrorState, LoadingState } from '@/components/app/states'
import { formatDateTime } from '@/components/app/format'
import { cn } from '@/lib/utils'

const DAYS = [1, 7, 30] as const

export const Route = createFileRoute('/ops/summary')({
  validateSearch: z.object({
    days: z.union([z.literal(1), z.literal(7), z.literal(30)]).catch(1),
  }),
  loaderDeps: ({ search }) => ({ days: search.days }),
  loader: ({ deps }) => opsApi<OpsSummary>(`/ops/summary?days=${deps.days}`),
  head: () => ({ meta: [{ title: 'Summary · Ops' }] }),
  component: SummaryPage,
  pendingComponent: () => <LoadingState label="Loading summary" />,
  errorComponent: ({ error }) => <ErrorState error={error} title="Could not load the summary" />,
})

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'bad' }) {
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn('text-xl font-semibold tabular-nums', tone === 'bad' && 'text-destructive')}>{value}</div>
    </div>
  )
}

function SummaryPage() {
  const s = Route.useLoaderData()
  const { days } = Route.useSearch()

  const funnelSteps = [
    { key: 'audits', label: 'Audits', value: s.funnel.audits },
    { key: 'previewRequests', label: 'Preview requests', value: s.funnel.previewRequests },
    { key: 'previewsReady', label: 'Previews ready', value: s.funnel.previewsReady },
    { key: 'paid', label: 'Paid', value: s.funnel.paid },
    { key: 'live', label: 'Live', value: s.funnel.live },
  ]
  const funnel = funnelSteps.map((step, i) => {
    const prev = i > 0 ? funnelSteps[i - 1].value : null
    return {
      key: step.key,
      label: step.label,
      value: step.value,
      display: formatInt(step.value),
      extra: prev === null ? '' : prev > 0 ? formatPct(step.value / prev) : '—',
    }
  })

  const stateRows = ORDER_STATES.filter((st) => (s.byState[st] ?? 0) > 0).map((st: OrderState) => ({
    key: st,
    label: STATE_LABELS[st],
    value: s.byState[st] ?? 0,
    display: formatInt(s.byState[st] ?? 0),
  }))

  const modelRows = [...s.costByModel]
    .sort((a, b) => b.costUsd - a.costUsd)
    .map((m) => ({ key: m.model, label: m.model, value: m.costUsd, display: formatUsd(m.costUsd), extra: `${formatUsd(m.costPerSiteUsd)} per site, ${formatInt(m.sites)} sites, ${formatInt(m.runs)} runs` }))
  const passRows = s.firstPassByModel.map((m) => ({ key: m.model, label: m.model, value: m.passRate, display: formatPct(m.passRate), extra: `${formatInt(m.reviews)} builds` }))

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-base font-semibold">Summary</h1>
        <nav aria-label="Period" className="inline-flex rounded-lg bg-muted p-0.5">
          {DAYS.map((d) => (
            <Link
              key={d}
              to="/ops/summary"
              search={{ days: d }}
              aria-current={d === days ? 'page' : undefined}
              className={cn(
                'rounded-md px-2.5 py-1 text-sm outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
                d === days ? 'bg-background font-medium shadow-sm' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {d === 1 ? '24 hours' : `${d} days`}
            </Link>
          ))}
        </nav>
        <span className="text-xs text-muted-foreground">
          {formatDateTime(s.from)} to {formatDateTime(s.to)}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6">
        <Stat label="Audits" value={formatInt(s.funnel.audits)} />
        <Stat label="Paid" value={formatInt(s.funnel.paid)} />
        <Stat label="First-pass review rate" value={formatPct(s.firstPassReviewRate)} />
        <Stat label="Average cost per order" value={s.avgCostUsd === null ? '—' : formatUsd(s.avgCostUsd)} />
        <Stat label="Refunds" value={formatInt(s.refunds)} tone={s.refunds > 0 ? 'bad' : undefined} />
        <Stat label="Needs human now" value={formatInt(s.needsHuman)} tone={s.needsHuman > 0 ? 'bad' : undefined} />
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <Section id="funnel" title="Funnel">
          {s.funnel.audits === 0 ? (
            <Muted>No audits in this period.</Muted>
          ) : (
            <BarTable caption="Funnel, orders per step with conversion from the previous step" rows={funnel} valueHeader="Count" extraHeader="From previous" />
          )}
        </Section>
        <Section id="cost-by-model" title="Cost by model">
          {modelRows.length === 0 ? (
            <Muted>No model runs in this period.</Muted>
          ) : (
            <BarTable caption="Model cost in USD, per site and number of runs" rows={modelRows} valueHeader="Cost" extraHeader="Per site" />
          )}
        </Section>
        <Section id="first-pass-by-model" title="First-pass review rate by model">
          {passRows.length === 0 ? (
            <Muted>No paid builds reviewed in this period.</Muted>
          ) : (
            <BarTable caption="Share of paid builds that passed their first review, by the model of the first fix round" rows={passRows} max={1} valueHeader="Pass rate" extraHeader="Builds" />
          )}
        </Section>
        <Section id="by-state" title="Orders by state" className="lg:col-span-2">
          {stateRows.length === 0 ? <Muted>No orders.</Muted> : <BarTable caption="Orders per state" rows={stateRows} valueHeader="Orders" />}
        </Section>
      </div>
    </div>
  )
}
